/**
 * Cloudflare KV Store Integration
 * Provides functions to sync local store data with Cloudflare KV storage
 */

import {
  getKVValue,
  setKVValue,
  isConfigured,
  checkProxyConnection,
} from "./cloudflareApi";
import { Token, CounterData } from "../types";

// Key constants for Cloudflare KV storage
const KV_KEYS = {
  TOKENS: "tokens",
  COUNTERS: "counters",
  MARKETING_COLUMNS: "marketingColumns",
};

// Debug flag
const DEBUG = true;

/**
 * Save tokens to Cloudflare KV
 */
export const saveTokensToCloudflare = async (
  tokens: Token[]
): Promise<boolean> => {
  if (!isConfigured()) {
    console.warn(
      "⚠️ Cloudflare KV not configured, skipping saveTokensToCloudflare"
    );
    return false;
  }

  if (DEBUG)
    console.log(`🔄 Saving ${tokens.length} tokens to Cloudflare KV...`);

  try {
    const result = await setKVValue(KV_KEYS.TOKENS, tokens);
    if (result) {
      if (DEBUG)
        console.log(
          `✅ Successfully saved ${tokens.length} tokens to Cloudflare KV`
        );
    } else {
      console.error(`❌ Failed to save tokens to Cloudflare KV`);
    }
    return result;
  } catch (error) {
    console.error("❌ Error saving tokens to Cloudflare KV:", error);
    return false;
  }
};

/**
 * Load tokens from Cloudflare KV
 */
export const loadTokensFromCloudflare = async (): Promise<Token[] | null> => {
  if (!isConfigured()) {
    console.warn(
      "⚠️ Cloudflare KV not configured, skipping loadTokensFromCloudflare"
    );
    return null;
  }

  if (DEBUG) console.log(`🔄 Loading tokens from Cloudflare KV...`);

  try {
    const tokens = await getKVValue<Token[]>(KV_KEYS.TOKENS);

    if (tokens) {
      if (DEBUG)
        console.log(
          `✅ Successfully loaded ${tokens.length} tokens from Cloudflare KV`
        );
      return tokens;
    } else {
      console.warn(`⚠️ No tokens found in Cloudflare KV or returned null`);
      return null;
    }
  } catch (error) {
    console.error("❌ Error loading tokens from Cloudflare KV:", error);
    return null;
  }
};

/**
 * Save counters to Cloudflare KV
 */
export const saveCountersToCloudflare = async (
  counters: CounterData
): Promise<boolean> => {
  if (!isConfigured()) {
    console.warn(
      "⚠️ Cloudflare KV not configured, skipping saveCountersToCloudflare"
    );
    return false;
  }

  if (DEBUG) console.log(`🔄 Saving counters to Cloudflare KV...`);

  try {
    const result = await setKVValue(KV_KEYS.COUNTERS, counters);
    if (result) {
      if (DEBUG) console.log(`✅ Successfully saved counters to Cloudflare KV`);
    } else {
      console.error(`❌ Failed to save counters to Cloudflare KV`);
    }
    return result;
  } catch (error) {
    console.error("❌ Error saving counters to Cloudflare KV:", error);
    return false;
  }
};

/**
 * Load counters from Cloudflare KV
 */
export const loadCountersFromCloudflare =
  async (): Promise<CounterData | null> => {
    if (!isConfigured()) {
      console.warn(
        "⚠️ Cloudflare KV not configured, skipping loadCountersFromCloudflare"
      );
      return null;
    }

    if (DEBUG) console.log(`🔄 Loading counters from Cloudflare KV...`);

    try {
      const counters = await getKVValue<CounterData>(KV_KEYS.COUNTERS);

      if (counters) {
        if (DEBUG)
          console.log(`✅ Successfully loaded counters from Cloudflare KV`);
        return counters;
      } else {
        console.warn(`⚠️ No counters found in Cloudflare KV or returned null`);
        return null;
      }
    } catch (error) {
      console.error("❌ Error loading counters from Cloudflare KV:", error);
      return null;
    }
  };

/**
 * Save marketing columns to Cloudflare KV
 */
export const saveMarketingColumnsToCloudflare = async (
  columns: string[]
): Promise<boolean> => {
  if (!isConfigured()) {
    console.warn(
      "⚠️ Cloudflare KV not configured, skipping saveMarketingColumnsToCloudflare"
    );
    return false;
  }

  if (DEBUG)
    console.log(
      `🔄 Saving ${columns.length} marketing columns to Cloudflare KV...`
    );

  try {
    const result = await setKVValue(KV_KEYS.MARKETING_COLUMNS, columns);
    if (result) {
      if (DEBUG)
        console.log(`✅ Successfully saved marketing columns to Cloudflare KV`);
    } else {
      console.error(`❌ Failed to save marketing columns to Cloudflare KV`);
    }
    return result;
  } catch (error) {
    console.error("❌ Error saving marketing columns to Cloudflare KV:", error);
    return false;
  }
};

/**
 * Load marketing columns from Cloudflare KV
 */
export const loadMarketingColumnsFromCloudflare = async (): Promise<
  string[] | null
> => {
  if (!isConfigured()) {
    console.warn(
      "⚠️ Cloudflare KV not configured, skipping loadMarketingColumnsFromCloudflare"
    );
    return null;
  }

  if (DEBUG) console.log(`🔄 Loading marketing columns from Cloudflare KV...`);

  try {
    const columns = await getKVValue<string[]>(KV_KEYS.MARKETING_COLUMNS);

    if (columns) {
      if (DEBUG)
        console.log(
          `✅ Successfully loaded ${columns.length} marketing columns from Cloudflare KV`
        );
      return columns;
    } else {
      console.warn(
        `⚠️ No marketing columns found in Cloudflare KV or returned null`
      );
      return null;
    }
  } catch (error) {
    console.error(
      "❌ Error loading marketing columns from Cloudflare KV:",
      error
    );
    return null;
  }
};

/**
 * Save all store data to Cloudflare KV
 */
export const saveAllToCloudflare = async (
  tokens: Token[],
  counters: CounterData,
  marketingColumns: string[]
): Promise<boolean> => {
  if (!isConfigured()) {
    console.warn(
      "⚠️ Cloudflare KV not configured, skipping saveAllToCloudflare"
    );
    return false;
  }

  if (DEBUG) console.log(`🔄 Saving all data to Cloudflare KV...`);

  try {
    // First check proxy connection
    const proxyAvailable = await checkProxyConnection();
    if (!proxyAvailable) {
      console.error(
        "❌ Proxy server is not available. Cannot save data to Cloudflare KV."
      );
      return false;
    }

    const results = await Promise.all([
      saveTokensToCloudflare(tokens),
      saveCountersToCloudflare(counters),
      saveMarketingColumnsToCloudflare(marketingColumns),
    ]);

    const [tokensResult, countersResult, columnsResult] = results;

    if (tokensResult && countersResult && columnsResult) {
      if (DEBUG) console.log(`✅ Successfully saved all data to Cloudflare KV`);
      return true;
    } else {
      console.error(
        `❌ Partial failure saving data to Cloudflare KV: tokens=${tokensResult}, counters=${countersResult}, columns=${columnsResult}`
      );
      return false;
    }
  } catch (error) {
    console.error("❌ Error saving all data to Cloudflare KV:", error);
    return false;
  }
};

/**
 * Load all store data from Cloudflare KV
 */
export const loadAllFromCloudflare = async (): Promise<{
  tokens: Token[] | null;
  counters: CounterData | null;
  marketingColumns: string[] | null;
}> => {
  if (!isConfigured()) {
    console.warn(
      "⚠️ Cloudflare KV not configured, skipping loadAllFromCloudflare"
    );
    return {
      tokens: null,
      counters: null,
      marketingColumns: null,
    };
  }

  if (DEBUG) console.log(`🔄 Loading all data from Cloudflare KV...`);

  try {
    // First check proxy connection
    const proxyAvailable = await checkProxyConnection();
    if (!proxyAvailable) {
      console.error(
        "❌ Proxy server is not available. Cannot load data from Cloudflare KV."
      );
      return {
        tokens: null,
        counters: null,
        marketingColumns: null,
      };
    }

    if (DEBUG)
      console.log("✅ Proxy server is available, proceeding with data load");

    try {
      const [tokens, counters, marketingColumns] = await Promise.all([
        loadTokensFromCloudflare(),
        loadCountersFromCloudflare(),
        loadMarketingColumnsFromCloudflare(),
      ]);

      // Log which data was loaded successfully
      if (DEBUG) {
        console.log("📊 Load results:");
        console.log(`- Tokens: ${tokens ? `${tokens.length} items` : "null"}`);
        console.log(`- Counters: ${counters ? "present" : "null"}`);
        console.log(
          `- Marketing columns: ${
            marketingColumns ? `${marketingColumns.length} items` : "null"
          }`
        );
      }

      return {
        tokens,
        counters,
        marketingColumns,
      };
    } catch (error) {
      console.error(
        "❌ Error in Promise.all during loadAllFromCloudflare:",
        error
      );
      throw error; // Re-throw to be caught by outer try/catch
    }
  } catch (error) {
    console.error("❌ Error loading all data from Cloudflare KV:", error);
    return {
      tokens: null,
      counters: null,
      marketingColumns: null,
    };
  }
};
